import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { supabase } from '../../supabaseClient';
import {
  getAircraftWbConstants,
  setAircraftWbConstants,
} from '../../aircraftWbConstantsStore';
import {
  findAircraftByRegistration,
  parseAircraftLookupCsv,
} from '../../aircraft/aircraftLookup';

function isMissingAircraftMethodColumnError(error) {
  const message = String(error?.message || '').toLowerCase();

  return (
    message.includes('recording_method') ||
    message.includes('column')
  );
}

function isMissingAircraftWbConstantsColumnError(error) {
  const message = String(error?.message || '').toLowerCase();

  return (
    message.includes('wb_constants') ||
    message.includes('column')
  );
}

function createDefaultLoadingStations(seatLayout = '4') {
  const stations = [
    {
      id: 'front-seats',
      name: 'Front Seats',
      type: 'occupants',
      arm: '',
      maxWeight: '',
    },
  ];

  if (String(seatLayout) !== '2') {
    stations.push({
      id: 'rear-seats',
      name: 'Rear Seats',
      type: 'occupants',
      arm: '',
      maxWeight: '',
    });
  }

  stations.push(
    {
      id: 'fuel',
      name: 'Fuel',
      type: 'fuel',
      arm: '',
      maxWeight: '',
    },
    {
      id: 'baggage',
      name: 'Baggage',
      type: 'baggage',
      arm: '',
      maxWeight: '',
    }
  );

  return stations;
}

function normaliseLoadingStations(
  loadingStations,
  seatLayout = '4'
) {
  if (
    !Array.isArray(loadingStations) ||
    loadingStations.length === 0
  ) {
    return createDefaultLoadingStations(seatLayout);
  }

  return loadingStations.map((station, index) => ({
    id:
      station?.id ||
      `station-${Date.now()}-${index}`,
    name:
      String(station?.name || '').trim() ||
      `Loading Station ${index + 1}`,
    type: String(station?.type || 'other'),
    arm:
      station?.arm === null ||
      station?.arm === undefined
        ? ''
        : String(station.arm),
    maxWeight:
      station?.maxWeight === null ||
      station?.maxWeight === undefined
        ? ''
        : String(station.maxWeight),
  }));
}

function applySeatLayoutToStations(
  loadingStations,
  seatLayout
) {
  const layout = String(seatLayout || '4');

  let stations = normaliseLoadingStations(
    loadingStations,
    layout
  );

  if (layout === '2') {
    stations = stations.filter(
      (station) => station.id !== 'rear-seats'
    );
  }

  if (
    layout !== '2' &&
    !stations.some(
      (station) => station.id === 'rear-seats'
    )
  ) {
    const frontSeatIndex = stations.findIndex(
      (station) => station.id === 'front-seats'
    );

    const rearStation = {
      id: 'rear-seats',
      name: 'Rear Seats',
      type: 'occupants',
      arm: '',
      maxWeight: '',
    };

    if (frontSeatIndex >= 0) {
      stations.splice(frontSeatIndex + 1, 0, rearStation);
    } else {
      stations.unshift(rearStation);
    }
  }

  return stations;
}

function buildWbDraftForAircraft(aircraft) {
  const wb =
    aircraft?.wb_constants &&
    typeof aircraft.wb_constants === 'object'
      ? aircraft.wb_constants
      : {};

  const seatLayout = String(wb.seatLayout || '4');

  return {
    seatLayout,

    emptyWeight: Number.isFinite(
      Number(wb.emptyWeight)
    )
      ? String(wb.emptyWeight)
      : '',

    emptyMoment: Number.isFinite(
      Number(wb.emptyMoment)
    )
      ? String(wb.emptyMoment)
      : '',

    maxTakeoffWeight: Number.isFinite(
      Number(wb.maxTakeoffWeight)
    )
      ? String(wb.maxTakeoffWeight)
      : '',

    minCg: Number.isFinite(Number(wb.minCg))
      ? String(wb.minCg)
      : '',

    maxCg: Number.isFinite(Number(wb.maxCg))
      ? String(wb.maxCg)
      : '',

    loadingStations: applySeatLayoutToStations(
      wb.loadingStations,
      seatLayout
    ),
  };
}

async function resizeAircraftImage(
  file,
  maxSize = 1200,
  quality = 0.9
) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      image.src = reader.result;
    };

    reader.onerror = reject;
    image.onerror = reject;

    image.onload = () => {
      let { width, height } = image;

      if (width > height && width > maxSize) {
        height *= maxSize / width;
        width = maxSize;
      } else if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }

      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        reject(
          new Error(
            'Image canvas could not be created.'
          )
        );
        return;
      }

      context.drawImage(
        image,
        0,
        0,
        width,
        height
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(
              new Error('Image conversion failed.')
            );
            return;
          }

          resolve(
            new File(
              [blob],
              file.name.replace(
                /\.\w+$/,
                '.webp'
              ),
              {
                type: 'image/webp',
              }
            )
          );
        },
        'image/webp',
        quality
      );
    };

    reader.readAsDataURL(file);
  });
}

export default function useAircraftAdmin({
  organisationId,
  user,
  isAdmin,
}) {
  const [aircraftList, setAircraftList] =
    useState([]);

  const [aircraftDefects, setAircraftDefects] =
    useState([]);

  const [
    aircraftStatusMessage,
    setAircraftStatusMessage,
  ] = useState('');

  const [
    aircraftStatusError,
    setAircraftStatusError,
  ] = useState('');

  const [
    expandedStatusAircraftId,
    setExpandedStatusAircraftId,
  ] = useState('');

  const [
    aircraftStatusActionId,
    setAircraftStatusActionId,
  ] = useState('');

  const [regInput, setRegInput] = useState('');
  const [icaoType, setIcaoType] = useState('');
  const [adsbHex, setAdsbHex] = useState('');

  const [
    aircraftDetailsDraftMap,
    setAircraftDetailsDraftMap,
  ] = useState({});

  const [
    expandedWbAircraftId,
    setExpandedWbAircraftId,
  ] = useState('');

  const [wbDraftMap, setWbDraftMap] =
    useState({});

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const aircraftLookupRef = useRef([]);

  const fetchAircraft = useCallback(async () => {
    if (!organisationId) {
      setAircraftList([]);
      return;
    }

    let {
      data,
      error: fetchError,
    } = await supabase
      .from('aircraft')
      .select(
        'id, organisation_id, registration, icao_type, engine_count, image_url, recording_method, wb_constants, status, status_updated_at, status_updated_by, adsb_icao_hex'
      )
      .eq('organisation_id', organisationId)
      .order('id', {
        ascending: false,
      });

    if (
      fetchError &&
      (isMissingAircraftMethodColumnError(
        fetchError
      ) ||
        isMissingAircraftWbConstantsColumnError(
          fetchError
        ))
    ) {
      const fallback = await supabase
        .from('aircraft')
        .select(
          'id, organisation_id, registration, icao_type, engine_count, image_url, status, status_updated_at, status_updated_by, adsb_icao_hex'
        )
        .eq('organisation_id', organisationId)
        .order('id', {
          ascending: false,
        });

      data = (fallback.data || []).map(
        (row) => ({
          ...row,
          recording_method: 'hobbs',
          wb_constants:
            getAircraftWbConstants(row.id),
        })
      );

      fetchError = fallback.error;
    }

    if (fetchError) {
      setError('Could not load aircraft.');
      return;
    }

    setAircraftList(data || []);
  }, [organisationId]);

  const fetchAircraftDefects =
    useCallback(async () => {
      if (!isAdmin) {
        setAircraftDefects([]);
        return;
      }

      setAircraftStatusError('');

      const {
        data,
        error: defectsError,
      } = await supabase
        .from('aircraft_defects')
        .select(
          'id, aircraft_id, booking_id, reported_at, reported_by, severity, details, status, rectification, rectified_at, rectified_by'
        )
        .neq('status', 'rectified')
        .order('reported_at', {
          ascending: false,
        });

      if (defectsError) {
        setAircraftStatusError(
          'Could not load aircraft defects. Check the aircraft_defects table and RLS policies.'
        );
        return;
      }

      setAircraftDefects(data || []);
    }, [isAdmin]);

  useEffect(() => {
    let cancelled = false;

    async function loadAircraftReference() {
      try {
        const response = await fetch(
          '/aircraftReference.csv'
        );

        if (!response.ok) {
          throw new Error(
            'Aircraft reference file could not be loaded.'
          );
        }

        const text = await response.text();

        if (!cancelled) {
          aircraftLookupRef.current =
            parseAircraftLookupCsv(text);
        }
      } catch (_error) {
        if (!cancelled) {
          aircraftLookupRef.current = [];
        }
      }
    }

    loadAircraftReference();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchAircraft();
  }, [fetchAircraft]);

  useEffect(() => {
    fetchAircraftDefects();
  }, [fetchAircraftDefects]);

  function handleRegInputChange(event) {
    const rawValue = String(
      event?.target?.value || ''
    );

    const value = rawValue
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase();

    setRegInput(value);
    setError('');
    setSuccess('');

    if (!value) {
      setIcaoType('');
      setAdsbHex('');
      return;
    }

    const aircraft =
      findAircraftByRegistration(
        aircraftLookupRef.current,
        value
      );

    setIcaoType(aircraft?.icaoType || '');
    setAdsbHex(aircraft?.adsbHex || '');
  }

  async function handleAddAircraft(event) {
    event.preventDefault();

    setError('');
    setSuccess('');

    if (!organisationId) {
      setError(
        'Could not identify your organisation. Please sign in again.'
      );
      return;
    }

    if (!regInput.trim()) {
      setError(
        'Please enter an aircraft registration.'
      );
      return;
    }

    if (!icaoType) {
      setError(
        'Aircraft not recognised. Please check the registration and try again.'
      );
      return;
    }

    const registration = regInput
      .trim()
      .toUpperCase();

    // Query aircraft_type_engine_reference to get engine count
    let engineCount = null;
    let typeWasFound = false;

    const { data: refData, error: refError } =
      await supabase
        .from('aircraft_type_engine_reference')
        .select('engine_count')
        .eq('type_code', `${icaoType}`.toUpperCase().trim())
        .maybeSingle();

    if (!refError && refData) {
      engineCount = refData.engine_count;
      typeWasFound = true;
    }

    let { error: insertError } =
      await supabase
        .from('aircraft')
        .insert([
          {
            registration,
            organisation_id:
              organisationId,
            icao_type: icaoType,
            engine_count: engineCount,
            adsb_icao_hex:
              adsbHex || null,
            recording_method: 'hobbs',
            wb_constants: {},
          },
        ]);

    if (
      insertError &&
      (isMissingAircraftMethodColumnError(
        insertError
      ) ||
        isMissingAircraftWbConstantsColumnError(
          insertError
        ))
    ) {
      const fallback = await supabase
        .from('aircraft')
        .insert([
          {
            registration,
            organisation_id:
              organisationId,
            icao_type: icaoType,
            engine_count: engineCount,
            adsb_icao_hex:
              adsbHex || null,
          },
        ]);

      insertError = fallback.error;
    }

    if (insertError) {
      console.error(
        '[Add aircraft failed]',
        insertError
      );

      setError(
        `Could not add aircraft: ${String(
          insertError.message ||
          'Unknown database error'
        )}`
      );

      return;
    }

    if (typeWasFound) {
      setSuccess(
        engineCount === 2
          ? 'Aircraft added as multi-engine.'
          : 'Aircraft added as single-engine.'
      );
    } else {
      setSuccess(
        'Aircraft added. Please confirm its engine category in Aircraft Details.'
      );
    }

    setRegInput('');
    setIcaoType('');
    setAdsbHex('');

    await fetchAircraft();
  }

  function updateAircraftDetailsDraft(
    aircraftId,
    field,
    value
  ) {
    setAircraftDetailsDraftMap(
      (previous) => ({
        ...previous,
        [String(aircraftId)]: {
          ...(previous[
            String(aircraftId)
          ] || {}),
          [field]: value,
        },
      })
    );
  }

  async function handleSaveAircraftDetails(
    aircraft
  ) {
    const aircraftId = String(
      aircraft?.id || ''
    );

    if (!aircraftId) return;

    const draft =
      aircraftDetailsDraftMap[
        aircraftId
      ] || {};

    const nextAdsbHex = String(
      draft.adsb_icao_hex ??
        aircraft.adsb_icao_hex ??
        ''
    )
      .trim()
      .toUpperCase();

    // Normalize engine_count: empty -> null, "1" -> 1, "2" -> 2
    const rawEngineCount =
      draft.engine_count ??
      aircraft.engine_count ??
      '';

    const engineCountStr = String(
      rawEngineCount
    ).trim();

    let nextEngineCount = null;

    if (engineCountStr === '') {
      nextEngineCount = null;
    } else if (engineCountStr === '1') {
      nextEngineCount = 1;
    } else if (engineCountStr === '2') {
      nextEngineCount = 2;
    } else {
      setError(
        'Please select Single-engine or Multi-engine.'
      );
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('');

    const { error: updateError } =
      await supabase
        .from('aircraft')
        .update({
          adsb_icao_hex:
            nextAdsbHex || null,
          engine_count: nextEngineCount,
        })
        .eq('id', aircraft.id)
        .eq(
          'organisation_id',
          organisationId
        );

    if (updateError) {
      setError(
        'Could not save aircraft details.'
      );
      return;
    }

    setAircraftList((previous) =>
      previous.map((item) =>
        Number(item.id) ===
        Number(aircraft.id)
          ? {
              ...item,
              adsb_icao_hex:
                nextAdsbHex,
              engine_count:
                nextEngineCount,
            }
          : item
      )
    );

    setSuccess(
      `Aircraft details saved for ${aircraft.registration}.`
    );
  }

  async function handleUpdateAircraftMethod(
    aircraftId,
    registration,
    recordingMethod
  ) {
    setError('');
    setSuccess('');

    const { error: updateError } =
      await supabase
        .from('aircraft')
        .update({
          recording_method:
            recordingMethod,
        })
        .eq('id', aircraftId)
        .eq(
          'organisation_id',
          organisationId
        );

    if (updateError) {
      if (
        isMissingAircraftMethodColumnError(
          updateError
        )
      ) {
        setError(
          'Aircraft recording method column is missing in Supabase.'
        );
      } else {
        setError(
          'Could not update aircraft recording method.'
        );
      }

      return;
    }

    setAircraftList((previous) =>
      previous.map((aircraft) =>
        Number(aircraft.id) ===
        Number(aircraftId)
          ? {
              ...aircraft,
              recording_method:
                recordingMethod,
            }
          : aircraft
      )
    );

    setSuccess(
      `Updated ${registration} recording method to ${String(
        recordingMethod || ''
      ).toUpperCase()}.`
    );
  }

  function toggleWbEditor(aircraft) {
    const aircraftId = String(
      aircraft?.id || ''
    );

    if (!aircraftId) return;

    if (
      expandedWbAircraftId === aircraftId
    ) {
      setExpandedWbAircraftId('');
      return;
    }

    setExpandedWbAircraftId(aircraftId);

    setWbDraftMap((previous) => {
      if (previous[aircraftId]) {
        return previous;
      }

      return {
        ...previous,
        [aircraftId]:
          buildWbDraftForAircraft(
            aircraft
          ),
      };
    });
  }

  function updateWbDraft(
    aircraftId,
    field,
    value
  ) {
    setWbDraftMap((previous) => {
      const aircraftKey =
        String(aircraftId);

      const currentDraft =
        previous[aircraftKey] || {
          seatLayout: '4',
          emptyWeight: '',
          emptyMoment: '',
          maxTakeoffWeight: '',
          minCg: '',
          maxCg: '',
          loadingStations:
            createDefaultLoadingStations(
              '4'
            ),
        };

      if (field === 'seatLayout') {
        return {
          ...previous,
          [aircraftKey]: {
            ...currentDraft,
            seatLayout: String(value),
            loadingStations:
              applySeatLayoutToStations(
                currentDraft.loadingStations,
                value
              ),
          },
        };
      }

      return {
        ...previous,
        [aircraftKey]: {
          ...currentDraft,
          [field]: value,
        },
      };
    });
  }

  async function handleSaveWbConstants(
    aircraft
  ) {
    const aircraftId = String(
      aircraft?.id || ''
    );

    if (!aircraftId) return;

    const draft =
      wbDraftMap[aircraftId] ||
      buildWbDraftForAircraft(aircraft);

    const requiredFields = [
      'emptyWeight',
      'emptyMoment',
      'maxTakeoffWeight',
      'minCg',
      'maxCg',
    ];

    const missingFields =
      requiredFields.filter(
        (field) =>
          !String(
            draft[field] ?? ''
          ).trim()
      );

    if (missingFields.length > 0) {
      setError(
        'Please complete all Weight & Balance constants before saving.'
      );
      setSuccess('');
      return;
    }

    const emptyWeight = Number(
  draft.emptyWeight
);

const emptyCg = Number(
  draft.emptyMoment
);

const emptyMoment =
  emptyWeight * emptyCg;

    const maxTakeoffWeight = Number(
      draft.maxTakeoffWeight
    );

    const minCg = Number(draft.minCg);
    const maxCg = Number(draft.maxCg);

    if (
      emptyWeight <= 0 ||
      emptyMoment <= 0 ||
      maxTakeoffWeight <= 0 ||
      minCg <= 0 ||
      maxCg <= 0
    ) {
      setError(
        'Weight & Balance figures must be greater than zero.'
      );
      setSuccess('');
      return;
    }

    if (
      maxTakeoffWeight <= emptyWeight
    ) {
      setError(
        'Maximum takeoff weight must be greater than empty weight.'
      );
      setSuccess('');
      return;
    }

    if (minCg >= maxCg) {
      setError(
        'Maximum CG must be greater than minimum CG.'
      );
      setSuccess('');
      return;
    }

    const loadingStations =
      normaliseLoadingStations(
        draft.loadingStations,
        draft.seatLayout
      );

    if (loadingStations.length === 0) {
      setError(
        'Please add at least one loading station.'
      );
      setSuccess('');
      return;
    }

    const invalidStation =
      loadingStations.find(
        (station) =>
          !String(
            station.name || ''
          ).trim() ||
          !Number.isFinite(
            Number(station.arm)
          ) ||
          Number(station.arm) <= 0
      );

    if (invalidStation) {
      setError(
        'Each loading station must have a name and an arm greater than zero.'
      );
      setSuccess('');
      return;
    }

    const payload = {
      seatLayout: String(
        draft.seatLayout || '4'
      ),

      emptyWeight,

      emptyMoment,

      maxTakeoffWeight,

      minCg,

      maxCg,

      loadingStations:
        loadingStations.map(
          (station, index) => ({
            id:
              station.id ||
              `station-${index + 1}`,

            name: String(
              station.name
            ).trim(),

            type: String(
              station.type || 'other'
            ),

            arm: Number(station.arm),

            maxWeight:
              String(
                station.maxWeight ?? ''
              ).trim() &&
              Number(
                station.maxWeight
              ) > 0
                ? Number(
                    station.maxWeight
                  )
                : null,
          })
        ),
    };

    setError('');
    setSuccess('');

    const { error: updateError } =
      await supabase
        .from('aircraft')
        .update({
          wb_constants: payload,
        })
        .eq('id', aircraft.id)
        .eq(
          'organisation_id',
          organisationId
        );

    if (updateError) {
      if (
        isMissingAircraftWbConstantsColumnError(
          updateError
        )
      ) {
        setAircraftWbConstants(
          aircraft.id,
          payload
        );

        setAircraftList((previous) =>
          previous.map((item) =>
            Number(item.id) ===
            Number(aircraft.id)
              ? {
                  ...item,
                  wb_constants:
                    payload,
                }
              : item
          )
        );

        setSuccess(
          `W&B constants saved locally for ${aircraft.registration}.`
        );
      } else {
        setError(
          'Could not save W&B constants.'
        );
      }

      return;
    }

    setAircraftList((previous) =>
      previous.map((item) =>
        Number(item.id) ===
        Number(aircraft.id)
          ? {
              ...item,
              wb_constants: payload,
            }
          : item
      )
    );

    setWbDraftMap((previous) => ({
      ...previous,
      [aircraftId]:
        buildWbDraftForAircraft({
          ...aircraft,
          wb_constants: payload,
        }),
    }));

    setSuccess(
      `W&B constants saved for ${aircraft.registration}.`
    );
  }

  async function handleRemoveAircraft(
    aircraftId,
    registration
  ) {
    setError('');
    setSuccess('');

    const confirmed = window.confirm(
      `Remove aircraft ${registration} from the fleet?`
    );

    if (!confirmed) return;

    const { error: deleteError } =
      await supabase
        .from('aircraft')
        .delete()
        .eq('id', aircraftId)
        .eq(
          'organisation_id',
          organisationId
        );

    if (deleteError) {
      setError(
        'Could not remove aircraft.'
      );
      return;
    }

    setSuccess(
      `Aircraft ${registration} removed.`
    );

    await fetchAircraft();
  }

  async function handleAircraftImageUpload(
    aircraft,
    file
  ) {
    if (!aircraft?.id || !file) return;

    setError('');
    setSuccess('');

    if (
      !file.type.startsWith('image/')
    ) {
      setError(
        'Please select an image file.'
      );
      return;
    }

    try {
      const resizedFile =
        await resizeAircraftImage(file);

      const filePath = `${aircraft.id}/main.webp`;

      const { error: uploadError } =
        await supabase.storage
          .from('aircraft-images')
          .upload(
            filePath,
            resizedFile,
            {
              upsert: true,
              contentType:
                'image/webp',
            }
          );

      if (uploadError) {
        setError(
          `Could not upload aircraft image: ${uploadError.message}`
        );
        return;
      }

      const { data } =
        supabase.storage
          .from('aircraft-images')
          .getPublicUrl(filePath);

      const imageUrl =
        data?.publicUrl || '';

      const { error: updateError } =
        await supabase
          .from('aircraft')
          .update({
            image_url: imageUrl,
          })
          .eq('id', aircraft.id)
          .eq(
            'organisation_id',
            organisationId
          );

      if (updateError) {
        setError(
          'Image uploaded, but could not save it to the aircraft record.'
        );
        return;
      }

      setAircraftList((previous) =>
        previous.map((item) =>
          Number(item.id) ===
          Number(aircraft.id)
            ? {
                ...item,
                image_url: imageUrl,
              }
            : item
        )
      );

      setSuccess(
        `Aircraft image saved for ${aircraft.registration}.`
      );
    } catch (_error) {
      setError(
        'Could not process aircraft image.'
      );
    }
  }

  async function handleAircraftStatusChange(
    aircraftId,
    nextStatus
  ) {
    setAircraftStatusActionId(
      String(aircraftId)
    );

    setAircraftStatusMessage('');
    setAircraftStatusError('');

    const { error: updateError } =
      await supabase
        .from('aircraft')
        .update({
          status: nextStatus,
          status_updated_at:
            new Date().toISOString(),
          status_updated_by:
            user?.id || null,
        })
        .eq('id', aircraftId)
        .eq(
          'organisation_id',
          organisationId
        );

    if (updateError) {
      setAircraftStatusError(
        'Could not update aircraft status.'
      );

      setAircraftStatusActionId('');
      return;
    }

    setAircraftStatusMessage(
      `Aircraft status updated to ${String(
        nextStatus
      ).toUpperCase()}.`
    );

    await fetchAircraft();
    await fetchAircraftDefects();

    setAircraftStatusActionId('');
  }

  async function handleRectifyDefect(
    defect
  ) {
    const rectification =
      window.prompt(
        'Enter rectification details:',
        defect.rectification || ''
      );

    if (rectification === null) {
      return;
    }

    setAircraftStatusError('');
    setAircraftStatusMessage('');

    const { error: rectifyError } =
      await supabase
        .from('aircraft_defects')
        .update({
          status: 'rectified',
          rectification,
          rectified_at:
            new Date().toISOString(),
          rectified_by:
            user?.id || null,
        })
        .eq('id', defect.id);

    if (rectifyError) {
      setAircraftStatusError(
        'Could not rectify defect.'
      );
      return;
    }

    const remainingDefects =
      aircraftDefects.filter(
        (item) =>
          String(item.aircraft_id) ===
            String(
              defect.aircraft_id
            ) &&
          String(item.id) !==
            String(defect.id) &&
          String(
            item.status || ''
          ).toLowerCase() !==
            'rectified'
      );

    if (
      remainingDefects.length === 0
    ) {
      await supabase
        .from('aircraft')
        .update({
          status: 'serviceable',
          status_updated_at:
            new Date().toISOString(),
          status_updated_by:
            user?.id || null,
        })
        .eq(
          'id',
          defect.aircraft_id
        )
        .eq(
          'organisation_id',
          organisationId
        );
    }

    await fetchAircraft();
    await fetchAircraftDefects();

    setAircraftStatusMessage(
      'Defect rectified successfully.'
    );
  }

  return {
    aircraftList,
    aircraftDefects,

    aircraftStatusMessage,
    aircraftStatusError,

    expandedStatusAircraftId,
    setExpandedStatusAircraftId,

    aircraftStatusActionId,

    regInput,
    icaoType,
    adsbHex,

    aircraftDetailsDraftMap,

    expandedWbAircraftId,
    wbDraftMap,

    error,
    success,

    fetchAircraft,
    fetchAircraftDefects,

    handleRegInputChange,
    handleAddAircraft,

    updateAircraftDetailsDraft,
    handleSaveAircraftDetails,

    handleUpdateAircraftMethod,

    toggleWbEditor,
    updateWbDraft,
    handleSaveWbConstants,

    handleRemoveAircraft,
    handleAircraftImageUpload,

    handleAircraftStatusChange,
    handleRectifyDefect,
  };
}